import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import {
  Upload, Camera, ArrowRight, ArrowLeft, CheckCircle2, Phone, PhoneOff,
  Sparkles, Package, X, Zap, Copy,
} from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";
import { getGuestToken } from "@/lib/guestToken";

const CATEGORIES = [
  { k: "Imported Drinks", icon: "🥤" },
  { k: "Chocolates & Sweets", icon: "🍫" },
  { k: "Chips & Snacks", icon: "🍟" },
  { k: "Instant Foods", icon: "🍜" },
  { k: "Biscuits & Bakery", icon: "🍪" },
  { k: "Ice Cream & Frozen Treats", icon: "🍦" },
  { k: "Novelty Products", icon: "✨" },
  { k: "Other Products", icon: "📦" },
];
const POPSICLE_EMOJIS = ["🍭", "🍡", "🍦", "🍧", "🍨"];

export default function RequestProduct() {
  const prefersReduced = useReducedMotion();
  const [step, setStep] = useState(0); // 0 welcome, 1 details, 2 mobile, 3 success
  const [submitted, setSubmitted] = useState(null);
  const [form, setForm] = useState({ name: "", category: "", brand: "", description: "" });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imagePath, setImagePath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [wantsUpdates, setWantsUpdates] = useState(null);
  const [mobile, setMobile] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();

  const progress = step === 0 ? 0 : step === 1 ? 33 : step === 2 ? 66 : 100;

  const pickCategory = (c) => { setForm({ ...form, category: c }); };
  const canNextDetails = form.name.trim().length >= 2 && form.category;

  const onImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 6 * 1024 * 1024) return toast.error("Image too large (max 6MB)");
    setImageFile(f); setImagePreview(URL.createObjectURL(f));
    const fd = new FormData(); fd.append("file", f);
    setUploading(true);
    try {
      const { data } = await api.post("/wishlist/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImagePath(data.image_path);
    } catch (err) { toast.error(formatApiError(err)); setImageFile(null); setImagePreview(null); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (submitting) return;
    if (wantsUpdates && !/^[6-9]\d{9}$/.test(mobile.replace(/\D/g, "").slice(-10))) {
      return toast.error("Enter a valid 10-digit Indian mobile number");
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(), category: form.category,
        brand: form.brand.trim(), description: form.description.trim(),
        image_path: imagePath || "",
        guest_token: getGuestToken(),
        customer_mobile: wantsUpdates ? mobile.replace(/\D/g, "").slice(-10) : "",
        wants_updates: !!wantsUpdates,
      };
      const { data } = await api.post("/wishlist/requests", payload);
      setSubmitted(data);
      try {
        const stored = JSON.parse(localStorage.getItem("pp_my_requests") || "[]");
        const entry = { ref: data.public_ref, code: data.tracking_code, token: data.tracking_token, name: form.name.trim(), at: new Date().toISOString() };
        localStorage.setItem("pp_my_requests", JSON.stringify([entry, ...stored.filter(x => x.ref !== data.public_ref)].slice(0, 10)));
      } catch { /* ignore */ }
      setStep(3);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#FEED02] relative overflow-hidden" data-testid="request-product-page">
      {/* Floating popsicles bg */}
      {!prefersReduced && <FloatingBg />}

      {/* Progress strip */}
      {step > 0 && step < 3 && (
        <div className="fixed top-0 left-0 right-0 z-30 h-1 bg-black/10">
          <motion.div className="h-full bg-black" initial={false} animate={{ width: `${progress}%` }} transition={{ type: "spring", stiffness: 100 }} data-testid="progress-bar" />
        </div>
      )}

      <div className="relative z-10 max-w-xl mx-auto px-4 py-10 min-h-screen flex items-center">
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="welcome" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.5 }} className="w-full">
              <Welcome onStart={() => setStep(1)} />
            </motion.div>
          )}
          {step === 1 && (
            <motion.div key="details" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.35 }} className="w-full">
              <StepCard title="TELL US WHAT." subtitle="Name it. Brand it. Snap it (if you have a pic).">
                <div className="space-y-5">
                  <Field label="Product name *">
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Mogu Mogu Lychee, HiChew Mango…" className="arcade-input" data-testid="req-name" maxLength={120} autoFocus />
                  </Field>
                  <div>
                    <label className="arcade-label">Category *</label>
                    <div className="grid grid-cols-2 gap-2.5">
                      {CATEGORIES.map((c) => (
                        <button key={c.k} onClick={() => pickCategory(c.k)}
                          className={`text-left px-3 py-2.5 rounded-xl border-2 transition flex items-center gap-2 ${form.category === c.k ? "bg-black text-[#FEED02] border-black" : "bg-white border-black/10 hover:border-black"}`}
                          data-testid={`cat-${c.k.replace(/\W/g, "")}`}>
                          <span className="text-lg">{c.icon}</span>
                          <span className="text-xs font-medium">{c.k}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <Field label="Brand (optional)">
                    <input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Ramune, Nestle, Lays…" className="arcade-input" data-testid="req-brand" />
                  </Field>
                  <div>
                    <label className="arcade-label">Product photo (optional)</label>
                    <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={onImage} className="hidden" />
                    {imagePreview ? (
                      <div className="flex items-start gap-3 bg-white rounded-xl p-3 border-2 border-black/10">
                        <img src={imagePreview} alt="" className="w-20 h-20 rounded-lg object-cover" data-testid="image-preview" />
                        <div className="flex-1">
                          {uploading && <div className="text-xs text-black/60">Uploading…</div>}
                          {imagePath && <div className="text-xs text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={11} /> Uploaded</div>}
                          <button onClick={() => fileRef.current?.click()} className="arcade-btn-ghost text-[11px] mt-2">Replace</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => fileRef.current?.click()} className="arcade-btn-ghost w-full text-sm" data-testid="btn-upload-image"><Camera size={14} /> Snap a photo</button>
                    )}
                  </div>
                  <Field label="Anything else? (optional)">
                    <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Where you saw it, flavour, anything…" className="arcade-input" data-testid="req-description" maxLength={1000} />
                  </Field>
                </div>
                <div className="flex gap-2 justify-between pt-5">
                  <button onClick={() => setStep(0)} className="arcade-btn-ghost text-xs"><ArrowLeft size={12} /> Back</button>
                  <motion.button whileHover={{ scale: canNextDetails ? 1.03 : 1 }} whileTap={{ scale: canNextDetails ? 0.97 : 1 }} onClick={() => canNextDetails && setStep(2)} disabled={!canNextDetails || uploading} className="arcade-btn-dark text-sm disabled:opacity-30" data-testid="btn-next-details">
                    Next <ArrowRight size={14} />
                  </motion.button>
                </div>
              </StepCard>
            </motion.div>
          )}
          {step === 2 && (
            <motion.div key="mobile" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.35 }} className="w-full">
              <StepCard title="WANT A HEADS-UP?" subtitle="If this hits our shelves, we'll ping you. Totally optional.">
                {wantsUpdates === null && (
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => setWantsUpdates(true)} className="rounded-2xl bg-black text-[#FEED02] p-5 text-left" data-testid="btn-wants-updates">
                      <Phone size={22} className="mb-3" />
                      <div className="font-black text-lg leading-tight">YES, KEEP ME UPDATED</div>
                      <div className="text-[10px] opacity-70 mt-1">We'll send a quick SMS when it's in.</div>
                    </motion.button>
                    <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => setWantsUpdates(false)} className="rounded-2xl bg-white border-2 border-black p-5 text-left" data-testid="btn-no-updates">
                      <PhoneOff size={22} className="mb-3" />
                      <div className="font-black text-lg leading-tight">NO, MAYBE NEXT TIME</div>
                      <div className="text-[10px] opacity-70 mt-1">Submit anonymously.</div>
                    </motion.button>
                  </div>
                )}
                {wantsUpdates === true && (
                  <div className="mt-4 space-y-4">
                    <Field label="Indian mobile number">
                      <div className="flex items-stretch gap-2">
                        <span className="shrink-0 px-3 flex items-center rounded-xl font-mono font-bold bg-black text-[#FEED02] border-2 border-black">+91</span>
                        <input
                          type="tel" inputMode="numeric" autoComplete="tel-national"
                          value={mobile} onChange={(e) => setMobile(e.target.value.replace(/[^\d\s]/g, "").slice(0, 14))}
                          placeholder="98765 43210" maxLength={14}
                          className="flex-1 min-w-0 px-4 py-3 rounded-xl border-2 border-black/20 focus:border-black outline-none font-mono text-lg tracking-wider bg-white"
                          data-testid="mobile-input" />
                      </div>
                    </Field>
                    <div className="text-[11px] text-black/60">We'll only use this to tell you when the product is available. We never share your number.</div>
                    <button type="button" onClick={() => { setWantsUpdates(null); setMobile(""); }} className="arcade-btn-ghost text-[11px]">Change answer</button>
                  </div>
                )}
                {wantsUpdates === false && (
                  <div className="mt-4 p-4 rounded-xl bg-white border-2 border-black/10 text-sm">
                    No number — you're going anonymous. Good news: your request is still saved on this device under <strong>Track Your Request</strong>.
                    <button onClick={() => setWantsUpdates(null)} className="arcade-btn-ghost text-[11px] mt-3">Change answer</button>
                  </div>
                )}
                <div className="flex gap-2 justify-between pt-5">
                  <button onClick={() => setStep(1)} className="arcade-btn-ghost text-xs"><ArrowLeft size={12} /> Back</button>
                  <motion.button whileHover={{ scale: wantsUpdates !== null ? 1.03 : 1 }} whileTap={{ scale: wantsUpdates !== null ? 0.97 : 1 }}
                    onClick={submit} disabled={submitting || wantsUpdates === null}
                    className="arcade-btn-dark text-sm disabled:opacity-30" data-testid="btn-submit-request">
                    {submitting ? "Launching…" : (<>FIRE IT OFF <Zap size={14} /></>)}
                  </motion.button>
                </div>
              </StepCard>
            </motion.div>
          )}
          {step === 3 && submitted && (
            <SuccessScreen submitted={submitted} name={form.name} wantsUpdates={wantsUpdates} />
          )}
        </AnimatePresence>
      </div>

      <style>{`
        .arcade-input { width: 100%; padding: 12px 14px; border: 2px solid rgba(0,0,0,0.1); background: white; border-radius: 12px; font-size: 15px; outline: none; transition: all 0.15s; }
        .arcade-input:focus { border-color: #000; box-shadow: 0 0 0 3px rgba(0,0,0,0.08); }
        .arcade-label { display: block; font-size: 10px; text-transform: uppercase; letter-spacing: 0.15em; font-weight: 700; margin-bottom: 8px; color: rgba(0,0,0,0.65); }
        .arcade-btn-dark { display: inline-flex; align-items: center; gap: 8px; padding: 12px 20px; background: #000; color: #FEED02; border-radius: 999px; font-weight: 900; letter-spacing: 0.08em; text-transform: uppercase; border: 2px solid #000; }
        .arcade-btn-ghost { display: inline-flex; align-items: center; gap: 6px; padding: 10px 16px; background: transparent; color: #000; border-radius: 999px; font-weight: 700; border: 2px solid rgba(0,0,0,0.15); letter-spacing: 0.08em; text-transform: uppercase; }
        .arcade-btn-ghost:hover { border-color: #000; }
      `}</style>
    </div>
  );
}

/* ============== Welcome ============== */
function Welcome({ onStart }) {
  const prefersReduced = useReducedMotion();
  return (
    <div className="text-center">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.1 }}
        className="inline-block px-3 py-1 rounded-full bg-black text-[#FEED02] text-[10px] font-black tracking-[0.25em] mb-6">
        POP PANDA · HAPPINESS ON A STICK
      </motion.div>
      <motion.h1
        className="font-display font-black text-black leading-[0.9] tracking-[-0.02em]"
        style={{ fontSize: "clamp(44px, 12vw, 92px)" }}
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        CAN'T FIND<br />YOUR FAVE?
      </motion.h1>
      <motion.h2
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
        className="font-display font-black text-black/80 mt-3 leading-tight"
        style={{ fontSize: "clamp(22px, 5vw, 32px)" }}>
        MAKE IT HAPPEN.
      </motion.h2>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-5 mb-8 text-black/70 text-base max-w-sm mx-auto">
        Got something delicious in mind? Put it on our wishlist — if enough fans want it, we'll bring it in.
      </motion.p>
      <motion.button
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75 }}
        whileHover={prefersReduced ? {} : { scale: 1.05, rotate: -1 }} whileTap={{ scale: 0.95 }}
        onClick={onStart}
        className="relative inline-flex items-center gap-2 px-7 py-5 bg-black text-[#FEED02] rounded-full font-black text-base tracking-[0.1em] shadow-[6px_6px_0_rgba(0,0,0,0.9)] hover:shadow-[3px_3px_0_rgba(0,0,0,0.9)] hover:translate-x-[3px] hover:translate-y-[3px] transition-all"
        data-testid="btn-request-product">
        REQUEST A PRODUCT <Sparkles size={16} />
      </motion.button>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="mt-10 flex flex-col items-center gap-3">
        <Link to="/requested-products" className="text-xs font-black uppercase tracking-widest text-black/80 hover:text-black underline underline-offset-4" data-testid="btn-explore-wishlist">
          Explore Community Wishlist &rarr;
        </Link>
        <Link to="/track-request" className="text-xs text-black/60 hover:text-black" data-testid="link-track-hub">
          Already requested something? Track it &rarr;
        </Link>
      </motion.div>
    </div>
  );
}

/* ============== Step Card ============== */
function StepCard({ title, subtitle, children }) {
  return (
    <div className="rounded-3xl bg-white border-4 border-black p-6 sm:p-8 shadow-[8px_8px_0_rgba(0,0,0,0.9)]">
      <h2 className="font-display font-black leading-[0.95] tracking-[-0.01em]" style={{ fontSize: "clamp(28px, 6vw, 40px)" }}>{title}</h2>
      {subtitle && <p className="text-sm text-black/60 mt-1 mb-4">{subtitle}</p>}
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (<div><label className="arcade-label">{label}</label>{children}</div>);
}

/* ============== Success Screen ============== */
function SuccessScreen({ submitted, name, wantsUpdates }) {
  const prefersReduced = useReducedMotion();
  const [copied, setCopied] = useState(false);
  const code = submitted.tracking_code || submitted.public_ref;
  const copyCode = async () => {
    try { await navigator.clipboard.writeText(code); setCopied(true); toast.success("Tracker copied!"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Long-press to copy"); }
  };
  return (
    <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, type: "spring" }} className="w-full text-center relative" data-testid="request-confirmation">
      {!prefersReduced && <Confetti />}
      <motion.div initial={{ scale: 0, rotate: -45 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 0.15, type: "spring", stiffness: 180 }} className="inline-block mb-4 text-6xl">🍦</motion.div>
      <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
        className="font-type text-black leading-[0.95]" style={{ fontSize: "clamp(34px, 9vw, 56px)" }}>
        WISH LOCKED IN!
      </motion.h1>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }} className="mt-3 text-black/75 max-w-sm mx-auto">
        Your craving for <strong>{name}</strong> is officially on our radar.
      </motion.p>

      {/* Screenshot-friendly tracking card */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
        className="mt-6 mx-auto max-w-sm bg-white border-4 border-black rounded-3xl p-6 shadow-[8px_8px_0_rgba(0,0,0,0.9)]" data-testid="tracking-card">
        <div className="font-type text-[11px] tracking-[0.25em] text-black/60 mb-1">YOUR WISH HAS A TRACKER!</div>
        <div className="text-xs text-black/60 mb-3">Save this little code. It's your ticket to finding out where your wish is.</div>
        <div className="bg-black rounded-2xl py-4 px-3 mb-3">
          <div className="font-type text-[#FEED02] font-bold tracking-[0.12em]" style={{ fontSize: "clamp(22px, 6vw, 30px)" }} data-testid="tracking-code">{code}</div>
        </div>
        <button onClick={copyCode} className="arcade-btn-ghost text-xs w-full justify-center mb-2" data-testid="copy-tracker">
          {copied ? <CheckCircle2 size={13} /> : <Copy size={13} />} {copied ? "COPIED!" : "COPY MY TRACKER"}
        </button>
        <div className="font-type text-xs text-black/70 mt-3">SNAP IT. SAVE IT. TRACK IT. 🍦</div>
        <div className="text-[11px] text-black/55 mt-1">Take a screenshot or copy your tracking number. Use it anytime to check progress.</div>
      </motion.div>

      {submitted.status === "approved" && submitted.canonical_product && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.85 }} className="mt-4 inline-block px-4 py-2 bg-white border-2 border-black rounded-full text-xs font-bold">
          🎯 Someone already wished this! Your demand was added to <strong>{submitted.canonical_product.name}</strong>
        </motion.div>
      )}

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }} className="mt-6 flex flex-col gap-2 max-w-xs mx-auto">
        <Link to={`/track-request?code=${encodeURIComponent(code)}`} className="arcade-btn-dark w-full justify-center" data-testid="track-link">
          TRACK MY WISH <ArrowRight size={14} />
        </Link>
        <Link to="/request-product" onClick={() => window.location.reload()} className="arcade-btn-ghost w-full justify-center bg-white" data-testid="request-another">
          REQUEST ANOTHER PRODUCT
        </Link>
        <Link to="/requested-products" className="text-xs font-bold uppercase tracking-widest text-black/70 hover:text-black mt-1 underline underline-offset-4" data-testid="goto-wishlist">
          See the Community Wishlist &rarr;
        </Link>
      </motion.div>
      <div className="mt-6 text-[11px] text-black/55 max-w-sm mx-auto">
        {wantsUpdates
          ? "We'll ping your number when your wish lands on our shelves."
          : "No number? No worries. Just save your tracker!"}
      </div>
    </motion.div>
  );
}

/* ============== Decorations ============== */
function FloatingBg() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {Array.from({ length: 7 }).map((_, i) => (
        <motion.div key={i}
          className="absolute text-5xl opacity-20"
          style={{
            left: `${(i * 17 + 8) % 90}%`,
            top: `${(i * 23 + 10) % 85}%`,
          }}
          animate={{ y: [0, -20, 0], rotate: [0, 10, -10, 0] }}
          transition={{ duration: 6 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.5 }}>
          {POPSICLE_EMOJIS[i % POPSICLE_EMOJIS.length]}
        </motion.div>
      ))}
    </div>
  );
}
function Confetti() {
  const colors = ["#000", "#FEED02", "#FFF", "#000"];
  const pieces = useRef(Array.from({ length: 30 }).map((_, i) => ({
    x: (Math.random() - 0.5) * 500,
    y: -40 - Math.random() * 100,
    r: Math.random() * 360,
    c: colors[i % colors.length],
  }))).current;
  return (
    <div className="absolute inset-0 overflow-visible pointer-events-none top-0 left-1/2">
      {pieces.map((p, i) => (
        <motion.span key={i}
          initial={{ x: p.x, y: p.y, rotate: 0, opacity: 1 }}
          animate={{ y: 500 + Math.random() * 200, rotate: p.r + 720, opacity: 0 }}
          transition={{ duration: 2 + Math.random(), ease: "easeOut", delay: Math.random() * 0.2 }}
          className="absolute w-2 h-3 inline-block"
          style={{ background: p.c, left: 0, top: 0 }} />
      ))}
    </div>
  );
}
